import classNames from 'classnames'
import { CSSProperties, useEffect } from 'react'
import { Swiper as SwiperInterface } from 'swiper'

import { IProductCard } from '@src/components/product-card/product-card.types'
import BATCHED_EVENT_LIMIT from '@src/constants/batchedEventLimit'
import { Button, RichText, RichTextProps } from '@src/quince-components/src'
import LAYOUT_TYPES from '@src/quince-site-common/src/constants/layoutTypes'
import ButtonDomain from '@src/quince-site-common/src/domains/Button'
import { TrackingMetaData } from '@src/quince-site-common/src/domains/CustomSection'
import { batchedAnalytics } from '@src/quince-site-common/src/utils/batched-analytics'
import { getProductDataForBatchedAnalytics } from '@src/quince-site-common/src/utils/getProductDataForBatchedAnalytics'
import { getProductTagsDataForBatchedAnalytics } from '@src/quince-site-common/src/utils/getProductTagsDataForBatchedAnalytics'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import { TrackConfig } from '../../hocs/withTracking'

import { ProductListContent } from './productListContent'
import styles from './productListSectionTwo.module.scss'

interface ProductListSectionProps {
  heading?: string
  headingClassname?: string
  preHeadingRichTextProps?: RichTextProps
  headingRichTextProps?: RichTextProps
  subHeadingRichTextProps?: RichTextProps
  productCards: IProductCard[]
  wrapperClassName?: string
  productCardDesktopClassName?: string
  productCardMobileClassName?: string
  productCardWrapperMobileClassName?: string
  hideProductOptions?: boolean
  hideProductReviews?: boolean
  title?: string
  style?: CSSProperties
  layoutType?: LAYOUT_TYPES
  tracker?: string
  id?: string
  productClickTrackConfig?: TrackConfig
  numberOfSlidesToScroll?: number
  buttons?: ButtonDomain[]
  numberOfVisibleSlides?: number
  productCardExpClassName?: string
  chunkSize?: number
  infiniteScroll?: boolean
  showDots?: boolean
  wishlistIconOverrideClass?: string
  swiperClassName?: string
  hideWishlistIcon?: boolean
  hideProductName?: boolean
  gridColCount?: number
  isVertical?: boolean
  productPriceClass?: string
  skipTagRefresh?: boolean
  wishlistTrackConfig?: (productCard: IProductCard, idx: number) => Record<string, any>
  additionalProductTrackingContext?: (productCard: IProductCard, idx: number) => Record<string, any>
  carouselArrowClickTracker?: ({ direction }: { direction: string }) => void
  productCardLinkClass?: string
  hideBuyNowIcon?: boolean
  hideTags?: boolean
  productPriceWrapperClass?: string
  isNewDesign?: boolean
  productCardImageSectionClass?: string
  trackingMetaData?: TrackingMetaData
  prevIconClass?: string
  nextIconClass?: string
  afterChange?: (swiperInstance: SwiperInterface) => void
}

function ProductListSectionTwo({
  heading,
  headingClassname,
  preHeadingRichTextProps,
  headingRichTextProps,
  subHeadingRichTextProps,
  wrapperClassName,
  productCardDesktopClassName,
  productCardMobileClassName,
  hideProductOptions,
  hideProductReviews,
  layoutType = LAYOUT_TYPES.DEFAULT,
  title,
  style,
  tracker,
  id,
  productClickTrackConfig,
  productCards,
  buttons = [],
  productCardExpClassName,
  infiniteScroll = false,
  wishlistIconOverrideClass,
  swiperClassName,
  hideWishlistIcon,
  hideProductName,
  gridColCount,
  skipTagRefresh = false,
  wishlistTrackConfig = () => ({}),
  additionalProductTrackingContext = () => ({}),
  carouselArrowClickTracker,
  productPriceClass,
  productCardLinkClass,
  hideBuyNowIcon,
  hideTags,
  isNewDesign,
  productCardImageSectionClass,
  trackingMetaData,
  productCardWrapperMobileClassName,
  chunkSize,
  prevIconClass,
  nextIconClass,
  afterChange,
}: ProductListSectionProps) {
  const componentUniqueIdentifier = productClickTrackConfig?.context?.id as string
  const productViewTracker =
    (tracker && `${tracker}_${componentUniqueIdentifier}__productListSectionTwo`) ?? 'productListSectionTwo'

  useEffect(() => {
    if (productViewTracker && productClickTrackConfig?.context?.contentContainerType) {
      batchedAnalytics.initializeBatchedAnalyticsEvent({
        tracker: productViewTracker,
        eventLimit: BATCHED_EVENT_LIMIT[TELEMETRY_EVENT_NAMES.BULK_PRODUCT_CARD_VIEW],
        debouncePeriod: 1000,
      })
      batchedAnalytics.trackBatchedAnalytics({
        id: `internalAnalytics-${productViewTracker}`,
        tracker: productViewTracker,
        callback: function ({ events }) {
          const productList = events.map(event => getProductDataForBatchedAnalytics(event))
          const tagsCount = getProductTagsDataForBatchedAnalytics(productList)
          trackEvent(TELEMETRY_EVENT_NAMES.BULK_PRODUCT_CARD_VIEW, {
            productList: productList,
            tagsCount: tagsCount,
            ...productClickTrackConfig.context,
          })
        },
      })
    }
  }, [productViewTracker, productClickTrackConfig?.context])
  const YOUR_ESSENTIALS = 'your_essentials'

  const extractedText =
    preHeadingRichTextProps?.content?.content?.[0]?.content
      ?.map((c: any) => c.value)
      .join('')
      .trim() ?? ''

  return (
    <div
      id={title?.toLowerCase().split(' ').join('-')}
      className={classNames(styles.wrapper, styles[`wrapper__${layoutType}`], wrapperClassName)}
      style={style}
      data-testid={'productListSectionTwo'}
    >
      <div className={classNames(styles.textWrapper)}>
        {heading && <div className={headingClassname}>{heading}</div>}
        {preHeadingRichTextProps && (
          <RichText {...preHeadingRichTextProps} className={classNames(preHeadingRichTextProps.className)} />
        )}
        {headingRichTextProps && (
          <RichText {...headingRichTextProps} className={classNames(headingRichTextProps.className)} />
        )}
        {subHeadingRichTextProps && (
          <RichText {...subHeadingRichTextProps} className={classNames(subHeadingRichTextProps.className)} />
        )}
        {buttons.map(button => (
          <Button key={button.id} {...ButtonDomain.makeButtonProps(button)}>
            {button.label}
          </Button>
        ))}
      </div>
      <ProductListContent
        productCards={productCards}
        infiniteScroll={infiniteScroll}
        swiperClassName={swiperClassName}
        gridColCount={gridColCount}
        tracker={tracker}
        id={id}
        productCardDesktopClassName={productCardDesktopClassName}
        productCardExpClassName={productCardExpClassName}
        productCardMobileClassName={productCardMobileClassName}
        wishlistIconOverrideClass={wishlistIconOverrideClass}
        hideProductReviews={hideProductReviews}
        hideProductOptions={hideProductOptions}
        hideWishlistIcon={hideWishlistIcon}
        productClickTrackConfig={
          productClickTrackConfig
            ? {
                ...productClickTrackConfig,
                context: {
                  ...productClickTrackConfig.context,
                  tabData: {
                    ...(productClickTrackConfig.context?.tabData || {}),
                    rec_element_name: extractedText || YOUR_ESSENTIALS,
                    rec_product_count: (productCards?.length ?? 0).toString(),
                  },
                },
              }
            : undefined
        }
        hideProductName={hideProductName}
        productCardLinkClass={productCardLinkClass}
        hideBuyNowIcon={hideBuyNowIcon}
        hideTags={hideTags}
        skipTagRefresh={skipTagRefresh}
        wishlistTrackConfig={wishlistTrackConfig}
        additionalProductTrackingContext={additionalProductTrackingContext}
        productViewTracker={productViewTracker}
        productPriceClass={productPriceClass}
        isNewDesign={isNewDesign}
        productCardImageSectionClass={productCardImageSectionClass}
        trackingMetaData={trackingMetaData}
        carouselArrowClickTracker={carouselArrowClickTracker}
        productCardWrapperMobileClassName={productCardWrapperMobileClassName}
        chunkSize={chunkSize}
        prevIconClass={prevIconClass}
        nextIconClass={nextIconClass}
        afterChange={afterChange}
      />
    </div>
  )
}

export default ProductListSectionTwo
